import { EnhancedStockData } from '@/types/deals';
import { StockData } from '@/types';
import { apiClient } from './client';
import { enhanceStocksWithDealSummaries } from '@/utils/dealAggregation';

// Interface for client deals response
export interface ClientDeal {
  isin: string;
  stock_name: string;
  client_name: string;
  client_category: string;
  exchange: string;
  deal_type: string;
  action: string;
  reported_date: string;
  average_price: number;
  quantity: number;
  traded_percentage: number | null;
  last_traded_price: number;
  post_transaction_holding: number;
  period: string;
  security_type: string;
  mode: string;
  regulation_type: string;
}

export interface ClientDealsResponse {
  client_name: string;
  total_deals: number;
  deals: ClientDeal[];
}

export interface ClientNamesResponse {
  client_names: string[];
}

/**
 * Fetch deals based on filter criteria and enhance with aggregated data
 */
export const getDeals = async (filters: Record<string, unknown> = {}): Promise<EnhancedStockData[]> => {
  // Clean up and prepare the filters
  const cleanedFilters: Record<string, unknown> = {};
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      cleanedFilters[key] = value;
    }
  });

  const response = await apiClient.get('/market/deals/stock-view/', {
    params: cleanedFilters
  });
  
  // Type assertion to handle the unknown type
  const responseData = response.data as { data?: { stocks?: unknown[] } };
  
  // Handle the nested structure of the API response
  const stocks = responseData.data?.stocks || [];
  
  // Enhance the stocks with aggregated deal information
  // Use StockData[] type which is what the function expects
  return enhanceStocksWithDealSummaries(stocks as StockData[]);
};

/**
 * Fetch all deals for a specific client
 */
export const getClientDeals = async (clientName: string): Promise<ClientDealsResponse> => {
  const response = await apiClient.get('/market/deals/client/', {
    params: { client_name: clientName }
  });
  
  // Type the response data properly
  const responseData = response.data as { data: ClientDealsResponse };
  return responseData.data;
};

/**
 * Fetch all client names for search suggestions
 */
export const getClientNames = async (): Promise<string[]> => {
  const response = await apiClient.get('/market/deals/client-names/');
  
  // Type the response data properly
  const responseData = response.data as { data: ClientNamesResponse };
  return responseData.data.client_names.filter(name => name && name.trim() !== '' && name !== '-');
}; 